import {
  isAnalyticsAllowed,
  type CookieConsent,
} from "./cookieConsentService";
import type { RegionBucket } from "./regionService";

export type AnalyticsReadinessStatus =
  | "ready"
  | "disabled_in_dev"
  | "blocked_by_consent"
  | "blocked_by_region_unavailable"
  | "misconfigured"
  | "init_error";

export type AnalyticsDispatchResult = {
  sent: boolean;
  status: AnalyticsReadinessStatus;
  reason?: string;
  runtimeInitialized?: boolean;
  transportHealth?: "unknown" | "healthy" | "unhealthy";
  transportReason?: string;
};

export type AnalyticsGateInput = {
  isDev: boolean;
  measurementId?: string;
  regionBucket?: RegionBucket;
  consent?: CookieConsent | null;
};

export function resolveAnalyticsReadiness(
  input: AnalyticsGateInput
): Omit<AnalyticsDispatchResult, "sent"> {
  if (input.isDev) {
    return {
      status: "disabled_in_dev",
      reason: "disabled in development",
    };
  }

  if (!input.measurementId?.trim()) {
    return {
      status: "misconfigured",
      reason: "missing VITE_FIREBASE_MEASUREMENT_ID",
    };
  }

  if (!input.regionBucket) {
    return {
      status: "blocked_by_region_unavailable",
      reason: "region information unavailable",
    };
  }

  if (!isAnalyticsAllowed(input.regionBucket, input.consent ?? null)) {
    return {
      status: "blocked_by_consent",
      reason: `analytics consent blocked for region ${input.regionBucket}`,
    };
  }

  return { status: "ready" };
}

export function getAnalyticsQueueDecision(
  result: AnalyticsDispatchResult
): "queue" | "drop" {
  return result.status === "blocked_by_region_unavailable" ? "queue" : "drop";
}

export function shouldMarkFirstVisitAnalyticsDispatched(
  eventName: string,
  result: AnalyticsDispatchResult,
  alreadySent: boolean
): boolean {
  return eventName === "first_visit" && result.sent && !alreadySent;
}
